import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type { ReportsSummaryResponse } from '@eriko/shared-types';
import { EMPTY_SUMMARY, EMPTY_TAX, usePeriodState } from '@eriko/ui/src/reports';
import { downloadReportCsv, downloadReportPdf, getReportsSummary } from '../api/reports';

/**
 * Per-client reports hook for the admin Reviewer Preview tab.
 *
 * Mirrors the SME dashboard `useReports` shape but scopes every call to the
 * passed `orgId` (admin JWT is `__internal__`; the backend `resolveTargetOrg`
 * accepts `?orgId=` for REVIEWER / PLATFORM_ADMIN — see routes/reports.ts).
 *
 * Send-email is intentionally NOT exposed here: a reviewer emailing themselves
 * an SME's books defeats the "preview before sign-off" purpose. The admin
 * `ReportsTab` omits the Send Email button accordingly. Reviewers download the
 * PDF/CSV if they need a personal copy.
 */
export function useOrgReports(orgId: string) {
  const periodState = usePeriodState('this_month');
  const { period, customRange } = periodState;

  const queryParams =
    period === 'custom'
      ? customRange
        ? { orgId, period, from: customRange.from, to: customRange.to }
        : null
      : { orgId, period };

  const query = useQuery<ReportsSummaryResponse>({
    queryKey: ['org-reports-summary', orgId, period, customRange?.from, customRange?.to],
    queryFn: () => getReportsSummary(queryParams!),
    staleTime: 60_000,
    enabled: queryParams !== null,
    placeholderData: keepPreviousData,
  });

  // Defense in depth: if `period === 'custom'` and no range is set, refuse
  // to fire the download. The `ReportsMasthead` already disables the buttons
  // via `needsCustomRange`, but a stale `mutateAsync` reference from a
  // future caller would otherwise hit the backend with `period=custom`
  // missing `from`/`to`, surface a confusing "Failed to build PDF" toast.
  const downloadPdfMutation = useMutation<string, Error, void>({
    mutationFn: () => {
      if (!queryParams) {
        return Promise.reject(new Error('Pick a date range before downloading.'));
      }
      return downloadReportPdf(queryParams);
    },
  });
  const downloadCsvMutation = useMutation<string, Error, void>({
    mutationFn: () => {
      if (!queryParams) {
        return Promise.reject(new Error('Pick a date range before downloading.'));
      }
      return downloadReportCsv(queryParams);
    },
  });

  const data = query.data;

  return {
    ...periodState,
    periodLabel: data?.period.label ?? periodState.periodLabels[period],
    summary: data?.summary ?? EMPTY_SUMMARY,
    categories: data?.categories ?? [],
    taxEstimate: data?.taxEstimate ?? EMPTY_TAX,
    trend: data?.trend ?? [],
    transactions: data?.largest ?? [],
    isLoading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    isFetching: query.isFetching,
    error: query.error,
    downloadPdf: downloadPdfMutation.mutateAsync,
    isDownloadingPdf: downloadPdfMutation.isPending,
    downloadCsv: downloadCsvMutation.mutateAsync,
    isDownloadingCsv: downloadCsvMutation.isPending,
  };
}
